import { useMemo, useRef, useState } from 'react';

import { UpdateUserProfileRequestDTOSchema } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { PhoneCheckContext } from '@site-mate/sitemate-global-shared/lib';
import { TimezoneMapping } from '@site-mate/sitemate-global-shared/lib/data-list/timezones';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { SignatureIcon } from '@/components/icons/signature';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Form, FormFieldItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toaster';
import { env } from '@/environment';
import { cn } from '@/lib/utils';
import { getSitemateUserQueryOptions } from '@/queries/assets';
import { ApiService } from '@/services/api-v2';
import { signupOtpService } from '@/services/global-api/signup-otp.service';
import { useUserStore } from '@/stores/user.store';

import { PhoneVerificationErrorReason, PhoneVerificationStatus, PhoneVerificationStep } from '../signup/sign-up-phone-verification';
import {
  fetchV3TokenWithRetry,
  isSamePhoneNumber,
  mapSendErrorReason,
  mapVerifyErrorReason,
  maskPhone,
  RECAPTCHA_FAILURE_MESSAGE,
  VALID_CAPTCHA_STATUS,
} from '../signup/signup-otp-utils';

dayjs.extend(utc);
dayjs.extend(tz);

type ProfileFormData = z.infer<typeof UpdateUserProfileRequestDTOSchema>;

const TOAST_DURATION_MS = 7000;

enum ProfileStep {
  Form = 'form',
  PhoneVerification = 'phone-verification',
}

function ProfilePage() {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={env.recaptcha.v3SiteKey}>
      <ProfilePageInner />
    </GoogleReCaptchaProvider>
  );
}

function ProfilePageInner() {
  const toast = useToast();
  const { user, setUser } = useUserStore();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [step, setStep] = useState<ProfileStep>(ProfileStep.Form);
  const [loading, setLoading] = useState(false);
  const [otpStatus, setOtpStatus] = useState<PhoneVerificationStatus>('entry');
  const [otpErrorReason, setOtpErrorReason] = useState<PhoneVerificationErrorReason | undefined>(undefined);
  const [otpCooldownSeconds, setOtpCooldownSeconds] = useState(0);

  const otpTwoFactorIdRef = useRef<string | null>(null);
  const verificationTokenRef = useRef<string | null>(null);
  const otpDeliveryMethodRef = useRef<'sms' | 'call'>('sms');

  const form = useForm<ProfileFormData>({
    defaultValues: {
      timezone: user?.timezone ?? '',
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      phoneNumber: user?.phone ?? '',
    },
    resolver: zodResolver(UpdateUserProfileRequestDTOSchema),
    mode: 'onChange',
  });

  const isSubmitButtonDisabled = useMemo(() => {
    return !form.formState.isValid || form.formState.isSubmitting || Object.keys(form.formState.errors).length > 0;
  }, [form.formState.isValid, form.formState.isSubmitting, form.formState.errors]);

  const { data: sitemateUser } = useQuery(getSitemateUserQueryOptions());

  const { mutateAsync: updateUserProfileMutate } = useMutation({
    mutationFn: async (data: ProfileFormData) => await (await ApiService.UpdateUserProfile(data)).json(),
    onSuccess: (data) => {
      toast.success('Profile updated successfully');
      // @ts-expect-error - TODO: replace this once we migrate to V2
      setUser({
        ...user,
        ...data.user,
      });
    },
    onError: () => {
      toast.error('Failed to update profile');
    },
  });

  async function commitProfile() {
    const data = form.getValues();
    const verificationToken = verificationTokenRef.current;
    // A token is minted for one specific number, so drop it either way — on success it is spent, and
    // on failure it must not ride along with whatever number the user tries next.
    verificationTokenRef.current = null;
    try {
      await updateUserProfileMutate({
        ...data,
        ...(verificationToken ? { verificationToken } : {}),
      });
    } catch {
      // The mutation's onError owns the message; callers only need us back on the form.
    } finally {
      setStep(ProfileStep.Form);
    }
  }

  async function onSubmit(data: ProfileFormData) {
    const phoneChanged = Boolean(data.phoneNumber) && !isSamePhoneNumber(data.phoneNumber, user?.phone);

    if (!phoneChanged) {
      await commitProfile();
      return;
    }

    setLoading(true);
    try {
      let clashResult: Awaited<ReturnType<typeof ApiService.CheckPhoneOnSignup>>;
      try {
        clashResult = await ApiService.CheckPhoneOnSignup({
          phone: data.phoneNumber as string,
          context: PhoneCheckContext.PhoneEdit,
          sitemateUserId: user?.sitemateUserId ?? user?.id,
        });
      } catch {
        // Fail open: a check-phone outage must not lock users out of editing their profile.
        await commitProfile();
        return;
      }

      const { clash, multipleEmailsDetected } = clashResult;

      if (clash && multipleEmailsDetected) {
        form.setError('phoneNumber', {
          type: 'multipleAccounts',
          message: 'Mobile number is already in use on another account.',
        });
        toast.error('This mobile number is already in use on another account. Please enter a different mobile number or contact support.', {
          duration: TOAST_DURATION_MS,
        });
        return;
      }

      if (!clash) {
        await commitProfile();
        return;
      }

      // Fail closed past this point: a confirmed clash must not commit without a passed OTP.
      try {
        await sendOtp('sms');
      } catch {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  // reCAPTCHA failed: one toast, return to the form (no OTP sent).
  function failVerification() {
    toast.error(RECAPTCHA_FAILURE_MESSAGE);
    setOtpStatus('entry');
    setStep(ProfileStep.Form);
  }

  async function sendOtp(deliveryMethod: 'sms' | 'call' = 'sms') {
    otpDeliveryMethodRef.current = deliveryMethod;
    const phone = form.getValues().phoneNumber as string;

    let recaptchaToken = '';
    if (executeRecaptcha) {
      try {
        recaptchaToken = await fetchV3TokenWithRetry(executeRecaptcha, 'send_otp');
      } catch {
        failVerification();
        return;
      }
    }

    let sendResult: Awaited<ReturnType<typeof signupOtpService.send>>;
    try {
      sendResult = await signupOtpService.send(phone, recaptchaToken, deliveryMethod, PhoneCheckContext.PhoneEdit);
    } catch (error) {
      const { reason, cooldownSeconds } = mapSendErrorReason(error);
      setOtpErrorReason(reason);
      setOtpCooldownSeconds(cooldownSeconds);
      setOtpStatus('error');
      setStep(ProfileStep.PhoneVerification);
      return;
    }

    if (sendResult.status !== VALID_CAPTCHA_STATUS || !sendResult.twoFactorId) {
      failVerification();
      return;
    }

    otpTwoFactorIdRef.current = sendResult.twoFactorId;
    setOtpErrorReason(undefined);
    setOtpCooldownSeconds(0);
    setOtpStatus('entry');
    setStep(ProfileStep.PhoneVerification);
  }

  async function onOtpVerify(code: string) {
    if (!otpTwoFactorIdRef.current) {
      setOtpErrorReason('expired');
      setOtpStatus('error');
      return;
    }

    setOtpErrorReason(undefined);
    setOtpStatus('submitting');
    try {
      const { verificationToken: token } = await signupOtpService.verify(otpTwoFactorIdRef.current, code);
      verificationTokenRef.current = token;
    } catch (error) {
      setOtpErrorReason(mapVerifyErrorReason(error));
      setOtpStatus('error');
      return;
    }

    try {
      await commitProfile();
    } finally {
      setOtpStatus('entry');
    }
  }

  function cancelVerification() {
    otpTwoFactorIdRef.current = null;
    // Drop the previous number's token so it can't be submitted against a different phone.
    verificationTokenRef.current = null;
    setOtpErrorReason(undefined);
    setOtpStatus('entry');
    setStep(ProfileStep.Form);
  }

  return (
    <>
      <div className="bg-grey-1 flex-1 px-4">
        <div className="mx-auto flex w-full max-w-[800px] flex-col gap-4 pt-4 pb-16">
          <div className="flex flex-col gap-4 rounded-md bg-white p-4 shadow-lg">
            <h1 className="text-xl font-medium">Profile</h1>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1">
                    <Label className={cn('text-xs')}>Email</Label>
                    <Input value={user?.email ?? ''} disabled />
                  </div>

                  <FormFieldItem control={form.control} name="firstName" label="First Name" required labelSize="small">
                    {({ value, onChange, error }) => (
                      <Input placeholder={`Enter first name...`} value={value as string} onChange={(e) => onChange(e.target.value)} error={!!error} />
                    )}
                  </FormFieldItem>
                  <FormFieldItem control={form.control} name="lastName" label="Last Name" required labelSize="small">
                    {({ value, onChange, error }) => (
                      <Input placeholder={`Enter last name...`} value={value as string} onChange={(e) => onChange(e.target.value)} error={!!error} />
                    )}
                  </FormFieldItem>
                  <FormFieldItem control={form.control} name="phoneNumber" label="Phone Number" required labelSize="small">
                    {({ value, onChange, error }) => (
                      <PhoneInput placeholder={`Enter phone number...`} value={value as string} onChange={(val) => onChange(val)} error={!!error} />
                    )}
                  </FormFieldItem>
                  <div className="flex flex-col gap-1">
                    <Label className={cn('text-xs')}>Signature</Label>
                    {sitemateUser?.signatureUrl ? (
                      <div className="border-grey-3 flex h-[110px] w-full max-w-[300px] items-center justify-center rounded border">
                        <img src={sitemateUser.signatureUrl} alt="Your signature" className="h-auto max-h-full object-contain" />
                      </div>
                    ) : (
                      <div className="text-secondary-text flex items-center gap-2 text-sm">
                        <SignatureIcon />
                        <span>No signature set. You can create one when signing a form.</span>
                      </div>
                    )}
                  </div>

                  <FormFieldItem control={form.control} name="timezone" label="Timezone" required labelSize="small">
                    {({ value, onChange, error }) => (
                      <Select
                        aria-label="Timezone"
                        options={timezoneOptions}
                        value={timezoneOptions.find((option) => option.value === value)}
                        onChange={(val) => onChange(val?.value)}
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: error ? 'red' : base.borderColor,
                          }),
                        }}
                      />
                    )}
                  </FormFieldItem>
                </div>

                <Button type="submit" variant="success" className="mt-4" loading={form.formState.isSubmitting || loading} disabled={isSubmitButtonDisabled}>
                  Save
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </div>

      <Dialog open={step === ProfileStep.PhoneVerification} onOpenChange={(open) => !open && cancelVerification()}>
        <DialogContent className="relative top-auto w-auto max-w-none border-0 bg-transparent p-0 shadow-none">
          <VisuallyHidden>
            <DialogTitle>Verify your phone number</DialogTitle>
          </VisuallyHidden>
          <PhoneVerificationStep
            status={otpStatus}
            errorReason={otpErrorReason}
            serverCooldownSeconds={otpCooldownSeconds}
            maskedPhone={maskPhone(form.getValues().phoneNumber as string)}
            onVerify={onOtpVerify}
            onResend={() => sendOtp('sms')}
            onRequestCall={() => sendOtp('call')}
            onUseDifferentNumber={cancelVerification}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ProfilePage;

const timezoneOptions = Object.keys(TimezoneMapping)
  .map((name) => {
    const offset = dayjs.tz(dayjs(), name).utcOffset();
    const label = `${name} (${formatOffset(offset)})`;
    return { value: name, label };
  })
  .sort((a, b) => a.label.localeCompare(b.label));

function formatOffset(minutes: number) {
  const sign = minutes >= 0 ? '+' : '-';
  const absMinutes = Math.abs(minutes);
  const hours = Math.floor(absMinutes / 60);
  const mins = absMinutes % 60;
  return `${sign}${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}
